import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BIVizDashboardAdEvents } from '../sql-maps/biviz-ad-events-datasource-sql-map';

export interface AdsARPUDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

// function verifyRequiredOptions(
//     options: IDataLoaderFactoryOptions
// ): options is AdsARPUDataLoaderOptions {
//     let isOk = true;
//     if (!options.id) {
//         isOk = false;
//         console.error('[AdsARPUDataLoaderOptions] Missing id.');
//     }
//     if (!options.funcService) {
//         isOk = false;
//         console.error(
//             '[AdsARPUDataLoaderOptions] Missing funcService.'
//         );
//     }
//     if (!options.gcpProjectId) {
//         isOk = false;
//         console.error(
//             '[AdsARPUDataLoaderOptions] Missing gcpProjectId'
//         );
//     }
//     return true;
//     // return options as SalesDatetimeDataLoaderOptions;
// }

export class AdsARPUDataLoaderFactory extends Factory<
    DataLoader,
    AdsARPUDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsARPUDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: AdsARPUDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log('AdsARPUDataLoaderFactory', queryContext);

                // ignore columns that are not supported in this query
                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                ];

                const adEventsDaily = BIVizDashboardAdEvents;
                // copy the sqlMap to avoid modifying the original
                const adEventsDailySqlMap = adEventsDaily.sqlMap;

                // delete unsupported column 'AD_CONSENT'
                delete adEventsDailySqlMap.columns.DAILY_AD_CONSENT;

                // identify columns not supported in datasource
                const adEventsDailyDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        adEventsDailySqlMap.columns
                    );

                const adEventsDailyDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        adEventsDailyDsColumns.missing
                    );

                //filter out ignored columns from dsColumns.present
                adEventsDailyDsColumns.present =
                    adEventsDailyDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const adEventsDailySQL =
                    adEventsDaily.compileSQLStatements(
                        adEventsDailyDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: ['day_no = 0'],
                // };

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                // TODO refactor datasource column names

                // if only cohort split group then 'cohort_split_group AS split_group'
                // if only ads flow split group then 'ads_flow_split_group AS split_group'
                // if both then 'CONCAT(cohort_split_group, ', ', ads_flow_split_group) AS split_group'
                // if none then ''

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH ads_flow AS
(
  SELECT
    ${adEventsDailySQL.splitSelectConcatCohortAdsFlow}
    *
  FROM 
  (
    SELECT
      ${adEventsDailySQL.cohortSplitSelect}
      ${adEventsDailySQL.adsFlowSplitSelect}

      day_no,  
      SUM(revenue_net_usd) AS revenue_usd,
      SUM(revenue_net_pln) AS revenue_pln
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        adEventsDailySqlMap.dataset,
        adEventsDailySqlMap.tableId
    )}\` ds
    ${adEventsDailySQL.join}
    ${adEventsDailySQL.where}
    GROUP BY ${adEventsDailySQL.cohortSplitGroupBy}${
        adEventsDailySQL.adsFlowSplitGroupBy
    }day_no
  )
)
${
    // cohort base korzysta z tabeli usersdash_activity i nie ma informacji o ads_flow_split_group
    // dlatego jeśli jest w context to musimy zrobić cross join
    // jeśli nie ma w context to nie ma sensu robić cross join
    // po cross join korzystamy z adEventsDailySQL
    ''
}
, cohort_base AS
(
    SELECT
      ${
          usersdashActivitySQL.splitGroupBy != ''
              ? `${usersdashActivitySQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      ds.*
      , MAX(day_no) OVER (PARTITION BY ${
          usersdashActivity.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
      }) AS max_day_no
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivitySQL.join}
    ${usersdashActivitySQL.where}
)

, cohort_size AS
(
  SELECT
    ${adEventsDailySQL.splitGroupBy}
    day_no,
    SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${adEventsDailySQL.splitSelectConcatCohortAdsFlow}
      ${
          BIVizDashboardAdEvents.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE
              ?.columnSQL
      },
      COUNT(DISTINCT player_id) AS cohort_size,
      GENERATE_ARRAY(0, max_day_no ) AS days
    FROM
    (
      SELECT cohort_base.*
      FROM cohort_base
      WHERE day_no = 0
    )
    ${
        adEventsDailySQL.is_in_context_split_ads_flow_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT ads_flow_split_group FROM ads_flow)`
            : ''
    }
    GROUP BY ${adEventsDailySQL.splitGroupBy}${
        BIVizDashboardAdEvents.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE
            ?.columnSQL
    }, max_day_no
  ), UNNEST(days) AS day_no 
  GROUP BY ${adEventsDailySQL.splitGroupBy}day_no
)

, by_fod AS
(
SELECT
  ${adEventsDailySQL.splitGroupBy != '' ? 'c.split_group,' : ''}
  c.day_no,
  
  ROUND(SUM(SAFE_DIVIDE(b.revenue_usd, c.cohort_size)) OVER (${
      adEventsDailySQL.splitGroupBy != ''
          ? 'PARTITION BY c.split_group '
          : ''
  }ORDER BY c.day_no), 2) AS ARPU_ad_usd,
  ROUND(SUM(SAFE_DIVIDE(b.revenue_pln, c.cohort_size)) OVER (${
      adEventsDailySQL.splitGroupBy != ''
          ? 'PARTITION BY c.split_group '
          : ''
  }ORDER BY c.day_no), 2) AS ARPU_ad_pln,
  FROM cohort_size c
  LEFT JOIN ads_flow b 
  ON c.day_no = b.day_no
  ${
      adEventsDailySQL.splitGroupBy != ''
          ? `AND c.split_group = b.split_group`
          : ''
  }
)
  
SELECT * FROM by_fod
ORDER BY ${adEventsDailySQL.splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: adEventsDailyDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        adEventsDailyDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
